import { Component, inject, Injector, OnInit } from '@angular/core';
import { UpdateCurrentMultiCurrencyCode } from '@app/finxone-web-frontend/app/lib/actions/account.action';
import { SafeHtmlPipe } from '@app/finxone-web-frontend/app/lib/pipes/SafeHtml.pipe';
import { AccountListingStrategiesType } from '@app/finxone-web-frontend/app/lib/strategies/interfaces/account-listing-strategy.type';
import { WidgetsStrategyProvider } from '@app/finxone-web-frontend/app/lib/strategies/widgets-strategy.provider';
import { filterIfHandlebarNotRendered } from '@app/finxone-web-frontend/app/lib/utils/template/model-builder.utils';
import { AutoDestroy } from '@finxone-platform/shared/base-types';
import {
  multiCurrencyDropdownTemplate,
  multiCurrencyVisualTemplate,
} from '@finxone-platform/shared/default-templates';
import {
  AccountTypes,
  ProviderClass,
  UiZoneWidgetAttributeConfig,
} from '@finxone-platform/shared/sys-config-types';
import { FinxoneSelectDropdownComponent } from '@finxone-platform/ui-components';
import { Store } from '@ngxs/store';
import Handlebars from 'handlebars';
import { Subject, takeUntil } from 'rxjs';
import { DropdownListingInterface } from '../accounts-context-dropdown-widget/accounts-context-dropdown-widget.component';
import { WidgetTemplateHelperService } from '@app/finxone-web-frontend/app/lib/services/widget-template-helper/widget-template-helper.service';
import { WidgetActionsSelector } from '@app/finxone-web-frontend/app/lib/utils/template/widget-template-helpers/action-to-handlebar-key-map';

const SELECT_ALL_KEY = 'All';

@Component({
  selector: 'finxone-web-frontend-multi-currency-widget',
  templateUrl: './multi-currency-widget.component.html',
  standalone: true,
  imports: [FinxoneSelectDropdownComponent, SafeHtmlPipe],
})
export class MultiCurrencyWidgetComponent implements OnInit {
  @AutoDestroy private destroy$: Subject<void> = new Subject<void>();
  // UI State
  public selectedMultiCurrencyAccount: { currency_code: string } = { currency_code: '' };
  public renderedMultiCurrencyContent = '';
  public selectedAccount: DropdownListingInterface | null = null;
  public dropdownListing: any[] = [];
  public isSearchEnabled = false;
  private isSelectAllLabelHidden = false;

  // Strategies & Logic
  private activeContextStrategy: AccountListingStrategiesType;

  // Templates
  public widgetVisualTemplate = multiCurrencyVisualTemplate;
  public widgetDropdownTemplate = multiCurrencyDropdownTemplate;
  private dropdownItemTemplateDelegate: HandlebarsTemplateDelegate<any>;
  private visualTemplateDelegate: HandlebarsTemplateDelegate<any>;

  private readonly widgetTemplateHelperService = inject(WidgetTemplateHelperService);

  public widgetProviderData = inject<ProviderClass<UiZoneWidgetAttributeConfig>>(ProviderClass);
  private readonly injector = inject(Injector);
  private readonly store = inject(Store);

  ngOnInit(): void {
    this.initializeTemplates();
    this.initializeStrategy();
    this.subscribeToAccountUpdates();
  }

  private initializeStrategy(): void {
    const strategyProvider = new WidgetsStrategyProvider(this.injector);
    this.activeContextStrategy = strategyProvider.getAccountListingStrategy(this.multiCurrencyAccountContext);
  }

  /**
   * Prepare Handlebars delegates
   */
  private initializeTemplates(): void {
    // Resolve templates from properties or defaults
    this.widgetVisualTemplate = this.widgetProperties?.['widgetVisualTemplate'] ?? this.widgetVisualTemplate;
    this.widgetDropdownTemplate =
      this.widgetProperties?.['widgetDropdownTemplate'] ?? this.widgetDropdownTemplate;

    this.isSearchEnabled = this.widgetProperties?.['isSearchEnabled'] ?? this.isSearchEnabled;

    // Compile templates
    if (this.widgetVisualTemplate) {
      this.visualTemplateDelegate = Handlebars.compile(this.widgetVisualTemplate);
    }
    if (this.widgetDropdownTemplate) {
      this.dropdownItemTemplateDelegate = Handlebars.compile(this.widgetDropdownTemplate);
    }
  }

  private subscribeToAccountUpdates(): void {
    if (!this.activeContextStrategy) return;

    this.activeContextStrategy
      .getAccountsStream()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.updateComponentState();
      });
  }

  /**
   * Core logic to recalculate state when the store emits
   */
  private updateComponentState(): void {
    // 1. Get Selected Account
    this.selectedAccount = this.activeContextStrategy.getSelectedAccountValueForDropdown(
      this.store,
      this.widgetProviderData.attri,
    );

    // 2. Build Dropdown List
    this.dropdownListing = this.buildDropdownList();

    // 3. Get Current Currency Code
    let currentCode = this.activeContextStrategy.getCurrentMultiCurrencyCode(this.store) ?? '';

    // reset the selected currency if select all label is hidden and stored currency value is All
    if (this.isSelectAllLabelHidden && currentCode === SELECT_ALL_KEY) {
      currentCode = '';
    }

    // When the "All" option is hidden there is no neutral entry to fall back on, so the
    // dropdown would render with nothing selected on first load (no stored currency yet).
    // Default to the stored currency when it is still available, otherwise the first
    // currency in the list, and persist it so the rest of the app stays in sync.
    if (this.isSelectAllLabelHidden) {
      const availableCurrencies = (this.dropdownListing as { currency: string }[]) ?? [];
      const isCurrentCodeAvailable = availableCurrencies.some((item) => item.currency === currentCode);
      if (!currentCode || !isCurrentCodeAvailable) {
        const fallbackCurrency = availableCurrencies[0]?.currency ?? '';
        if (fallbackCurrency && fallbackCurrency !== currentCode) {
          currentCode = fallbackCurrency;
          this.store.dispatch(new UpdateCurrentMultiCurrencyCode(currentCode));
        }
      }
    }

    this.selectedMultiCurrencyAccount = { currency_code: currentCode };

    // 4. Update Visual Render
    this.updateVisualRender();
  }

  private buildDropdownList() {
    this.isSelectAllLabelHidden = !!this.widgetProperties?.['isSelectAllLabelHidden'];

    const allCurrenciesLabel = this.isSelectAllLabelHidden ? [] : [{ currency: SELECT_ALL_KEY }];

    const accountCurrencies = (this.selectedAccount?.currency as { currency: string }[]) || [];
    return [...allCurrenciesLabel, ...accountCurrencies];
  }

  private updateVisualRender(): void {
    if (!this.visualTemplateDelegate) return;

    const context = {
      currencyItem: {
        currency: this.selectedMultiCurrencyAccount.currency_code,
      },
      ...this.selectedAccount,
    };

    this.renderedMultiCurrencyContent = filterIfHandlebarNotRendered(this.visualTemplateDelegate(context));
  }

  public onCurrencyChange(currencyCode: string): void {
    this.selectedMultiCurrencyAccount = { currency_code: currencyCode };
    this.store.dispatch(new UpdateCurrentMultiCurrencyCode(currencyCode));
    this.widgetTemplateHelperService.setWidgetAction(WidgetActionsSelector.SELECTED_ACCOUNT_CURRENCY_CHANGE);
  }

  public renderCurrencyListItem(currencyItem: { currency: string }): string {
    if (currencyItem?.currency === SELECT_ALL_KEY) {
      return currencyItem?.currency;
    }

    // Safety check if template compilation failed or didn't run
    if (!this.dropdownItemTemplateDelegate) return '';

    const { currency: _, ...rest } = (this.selectedAccount as DropdownListingInterface) || {};
    return filterIfHandlebarNotRendered(this.dropdownItemTemplateDelegate({ currencyItem, ...rest }));
  }

  public renderSelectedCurrencyItem(selectedAccount: DropdownListingInterface | null): string {
    if (!selectedAccount) return '';

    const currentCode = this.selectedMultiCurrencyAccount?.currency_code;

    if (currentCode === SELECT_ALL_KEY) {
      return currentCode;
    }

    if (!this.visualTemplateDelegate) return '';

    const { currency: _, ...rest } = selectedAccount;
    return filterIfHandlebarNotRendered(
      this.visualTemplateDelegate({
        currencyItem: { currency: currentCode ?? '' },
        ...rest,
      }),
    );
  }

  public get widgetProperties() {
    return this.widgetProviderData.attri?.widgetProperties;
  }

  public get multiCurrencyAccountContext(): AccountTypes {
    // Simplified using nullish coalescing
    return this.widgetProviderData.attri['multiCurrencyAccountContext'] ?? AccountTypes.CURRENT_ACCOUNT;
  }
}
